import { KYC_STATUS_ONBOARDING_NOT_FINISHED } from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { IdentificationStatusResponseKycStatus } from "@/services/model";
import { NamespaceKey } from "@/utils/translationKey";

const KYC_TODO_LABEL_PREFIX = "cockpit.dashboard.todos.kyc";

type KycTodoVariant = "internal" | "external" | "informational";

type PortalKey = NamespaceKey<"portal">;

type KycTodoConfig = {
  labelKey: PortalKey;
  detailKey?: PortalKey;
  variant: KycTodoVariant;
  href?: string;
  hrefKey?: PortalKey;
};

/** Failed ID checks the user can retry on the ID-check page. */
const RETRY_ID_CHECK_STATUSES = new Set<string>([
  IdentificationStatusResponseKycStatus.KYC_invalid_document,
  IdentificationStatusResponseKycStatus.KYC_ID_uncomparable,
  IdentificationStatusResponseKycStatus.KYC_error_processing_docs,
  IdentificationStatusResponseKycStatus.KYC_error_processing_selfie,
]);

/** Statuses with no actionable ID-check page — dashboard shows info or external links only. */
const NON_ACTIONABLE_ID_CHECK_STATUSES = new Set<string>([
  KYC_STATUS_ONBOARDING_NOT_FINISHED,
  IdentificationStatusResponseKycStatus.PENDING,
  IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING,
  IdentificationStatusResponseKycStatus.KYC_data_mismatch,
  IdentificationStatusResponseKycStatus.KYC_underaged,
  "KYC_max_attempts",
]);

export const isRetryIdCheckStatus = (kycStatus: string | undefined): boolean =>
  kycStatus !== undefined && RETRY_ID_CHECK_STATUSES.has(kycStatus);

/** No KYC result yet: status not loaded or onboarding still running. */
export const isKycUnsettled = (kycStatus: string | undefined): boolean =>
  !kycStatus || kycStatus === KYC_STATUS_ONBOARDING_NOT_FINISHED;

export const isIdCheckPageActionable = (
  kycStatus: string | undefined,
): boolean => !kycStatus || !NON_ACTIONABLE_ID_CHECK_STATUSES.has(kycStatus);

const idCheckTodo = (
  labelSuffix: NamespaceKey<"portal", typeof KYC_TODO_LABEL_PREFIX>,
): KycTodoConfig => ({
  labelKey: `${KYC_TODO_LABEL_PREFIX}.${labelSuffix}`,
  variant: "internal",
  href: portalRoutes.cockpit.idCheck,
});

const openTodo = idCheckTodo("open");

const KYC_TODO_CONFIGS: Record<string, KycTodoConfig | null> = {
  [KYC_STATUS_ONBOARDING_NOT_FINISHED]: null,
  [IdentificationStatusResponseKycStatus.OK]: null,
  [IdentificationStatusResponseKycStatus.OPEN]: openTodo,
  [IdentificationStatusResponseKycStatus.PENDING]: {
    labelKey: `${KYC_TODO_LABEL_PREFIX}.pending`,
    variant: "informational",
  },
  [IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING]: {
    labelKey: `${KYC_TODO_LABEL_PREFIX}.fraudCheckPending`,
    variant: "informational",
  },
  [IdentificationStatusResponseKycStatus.KYC_data_mismatch]: {
    labelKey: `${KYC_TODO_LABEL_PREFIX}.dataMismatch`,
    variant: "internal",
    href: portalRoutes.user.personal,
  },
  [IdentificationStatusResponseKycStatus.KYC_underaged]: {
    labelKey: `${KYC_TODO_LABEL_PREFIX}.underaged`,
    variant: "external",
    hrefKey: "cockpit.kycSection.links.underaged",
  },
  [IdentificationStatusResponseKycStatus.KYC_invalid_document]:
    idCheckTodo("invalidDocument"),
  [IdentificationStatusResponseKycStatus.KYC_ID_uncomparable]:
    idCheckTodo("idUncomparable"),
  [IdentificationStatusResponseKycStatus.KYC_error_processing_docs]:
    idCheckTodo("errorProcessingDocs"),
  [IdentificationStatusResponseKycStatus.KYC_error_processing_selfie]:
    idCheckTodo("errorProcessingSelfie"),
  KYC_max_attempts: {
    labelKey: `${KYC_TODO_LABEL_PREFIX}.maxAttempts`,
    variant: "external",
    hrefKey: "cockpit.kycSection.links.maxAttempts",
  },
};

/** Unknown statuses fall back to the OPEN todo. */
export const getKycTodoConfig = (
  kycStatus: string | undefined,
): KycTodoConfig | null => {
  if (kycStatus === undefined) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(KYC_TODO_CONFIGS, kycStatus)
    ? KYC_TODO_CONFIGS[kycStatus]
    : openTodo;
};
